import { type JSX, useEffect, useRef, useState } from 'react';

import type { MessageKeys } from '../i18n/messages/index.js';
import { useLanguage } from '../i18n/useLanguage.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { Button } from '../primitives/Button.js';
import {
  MyDataCategoryCard,
  MyDataEmptyNote,
  MyDataField,
} from '../routes/profile/MyDataCategoryCard.js';
import type {
  MyDataAnalytics,
  MyDataAnalyticsFact,
  MyDataAnalyticsPage,
  MyDataAnalyticsPageReader,
} from './MyDataPort.js';

export interface AnalyticsDataSectionProps {
  /** First page supplied by the consolidated `member.myData.view` read. */
  readonly analytics: MyDataAnalytics;
  /** The same request-scoped My Data adapter, used only for the next page. */
  readonly reader: MyDataAnalyticsPageReader;
}

type PagingPhase = 'idle' | 'loading' | 'error';

interface PagingState {
  readonly analytics: MyDataAnalytics;
  readonly reader: MyDataAnalyticsPageReader;
  readonly page: MyDataAnalyticsPage;
  readonly phase: PagingPhase;
}

const DEVICE_KEYS = {
  mobile: 'myData.analytics.device.mobile',
  tablet: 'myData.analytics.device.tablet',
  desktop: 'myData.analytics.device.desktop',
  unknown: 'myData.analytics.device.unknown',
} as const satisfies Record<MyDataAnalyticsFact['entryDevice'], MessageKeys>;

const SOURCE_KEYS = {
  direct: 'myData.analytics.source.direct',
  email: 'myData.analytics.source.email',
  search: 'myData.analytics.source.search',
  social: 'myData.analytics.source.social',
  paid: 'myData.analytics.source.paid',
  referral: 'myData.analytics.source.referral',
  unknown: 'myData.analytics.source.unknown',
} as const satisfies Record<MyDataAnalyticsFact['entrySource'], MessageKeys>;

const LANGUAGE_KEYS = {
  fi: 'myData.analytics.language.fi',
  sv: 'myData.analytics.language.sv',
  en: 'myData.analytics.language.en',
  unknown: 'myData.analytics.language.unknown',
} as const satisfies Record<MyDataAnalyticsFact['entryLanguage'], MessageKeys>;

const QUALITY_KEYS = {
  measured: 'myData.analytics.quality.measured',
  partial: 'myData.analytics.quality.partial',
  unavailable: 'myData.analytics.quality.unavailable',
} as const satisfies Record<MyDataAnalyticsFact['quality'], MessageKeys>;

const LOAD_KEYS = {
  document: 'myData.analytics.load.document',
  route: 'myData.analytics.load.route',
} as const satisfies Record<MyDataAnalyticsFact['loads'][number]['kind'], MessageKeys>;

const ERROR_KEYS = {
  render: 'myData.analytics.error.render',
  routeLoad: 'myData.analytics.error.routeLoad',
  resourceLoad: 'myData.analytics.error.resourceLoad',
  unhandled: 'myData.analytics.error.unhandled',
} as const satisfies Record<MyDataAnalyticsFact['errors'][number]['category'], MessageKeys>;

function formatDuration(milliseconds: number): string {
  if (milliseconds < 1_000) return `${milliseconds} ms`;
  const seconds = Math.floor(milliseconds / 1_000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return minutes === 0 ? `${seconds} s` : `${minutes} min ${remainingSeconds} s`;
}

function formatInstant(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const resolvedLocale = locale === 'sv' ? 'sv-SE' : locale === 'en' ? 'en-GB' : 'fi-FI';
  return new Intl.DateTimeFormat(resolvedLocale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function factKey(fact: MyDataAnalyticsFact): string {
  // The API intentionally withholds internal fact IDs. These safe fields are
  // the fact's visible identity; an exactly duplicated row has no UI state.
  return [
    fact.occurredAt,
    fact.pageLabel,
    fact.entryDevice,
    fact.entrySource,
    fact.entryLanguage,
    `referrerHost:${fact.attribution.referrerHost ?? '∅'}`,
    `campaignSource:${fact.attribution.campaignSource ?? '∅'}`,
    `campaignMedium:${fact.attribution.campaignMedium ?? '∅'}`,
    `campaignName:${fact.attribution.campaignName ?? '∅'}`,
    fact.activeMilliseconds ?? 'missing',
    fact.quality,
    ...fact.loads.map((load) => `${load.kind}:${load.durationMs}`),
    ...fact.errors.map((error) => `${error.occurredAt}:${error.category}`),
  ].join('|');
}

function AnalyticsFact({ fact }: { readonly fact: MyDataAnalyticsFact }): JSX.Element {
  const t = useTranslate();
  const { active: locale } = useLanguage();
  const loads =
    fact.loads.length === 0
      ? t('myData.analytics.loads.none')
      : fact.loads
          .map((load) => `${t(LOAD_KEYS[load.kind])}: ${formatDuration(load.durationMs)}`)
          .join(', ');
  const errors =
    fact.errors.length === 0
      ? t('myData.analytics.errors.none')
      : fact.errors
          .map(
            (error) =>
              `${t(ERROR_KEYS[error.category])} (${formatInstant(error.occurredAt, locale)})`,
          )
          .join(', ');

  return (
    <article className="border-b border-border last:border-b-0">
      <dl className="flex flex-col">
        <MyDataField
          label={t('myData.analytics.occurredAt')}
          value={formatInstant(fact.occurredAt, locale)}
        />
        <MyDataField label={t('myData.analytics.page')} value={fact.pageLabel} />
        <MyDataField
          label={t('myData.analytics.entryDevice')}
          value={t(DEVICE_KEYS[fact.entryDevice])}
        />
        <MyDataField
          label={t('myData.analytics.entrySource')}
          value={t(SOURCE_KEYS[fact.entrySource])}
        />
        <MyDataField
          label={t('myData.analytics.referrerHost')}
          value={fact.attribution.referrerHost ?? t('myData.analytics.attribution.unavailable')}
        />
        <MyDataField
          label={t('myData.analytics.campaignSource')}
          value={fact.attribution.campaignSource ?? t('myData.analytics.attribution.unavailable')}
        />
        <MyDataField
          label={t('myData.analytics.campaignMedium')}
          value={fact.attribution.campaignMedium ?? t('myData.analytics.attribution.unavailable')}
        />
        <MyDataField
          label={t('myData.analytics.campaignName')}
          value={fact.attribution.campaignName ?? t('myData.analytics.attribution.unavailable')}
        />
        <MyDataField
          label={t('myData.analytics.entryLanguage')}
          value={t(LANGUAGE_KEYS[fact.entryLanguage])}
        />
        <MyDataField
          label={t('myData.analytics.active')}
          value={
            fact.activeMilliseconds === null
              ? t('myData.analytics.active.missing')
              : formatDuration(fact.activeMilliseconds)
          }
        />
        <MyDataField label={t('myData.analytics.loads')} value={loads} />
        <MyDataField label={t('myData.analytics.errors')} value={errors} />
        <MyDataField label={t('myData.analytics.quality')} value={t(QUALITY_KEYS[fact.quality])} />
      </dl>
    </article>
  );
}

export function AnalyticsDataSection({
  analytics,
  reader,
}: AnalyticsDataSectionProps): JSX.Element {
  const t = useTranslate();
  const [state, setState] = useState<PagingState>({
    analytics,
    reader,
    page: analytics,
    phase: 'idle',
  });
  const requestId = useRef(0);
  const activeScope = useRef({ analytics, reader });

  // A new account/adapter receives its first page through `view`. Reset the
  // local page during render so a prior account's facts never commit onscreen.
  if (state.analytics !== analytics || state.reader !== reader) {
    setState({ analytics, reader, page: analytics, phase: 'idle' });
  }

  useEffect(() => {
    activeScope.current = { analytics, reader };
    return () => {
      requestId.current += 1;
    };
  }, [analytics, reader]);

  const current =
    state.analytics === analytics && state.reader === reader
      ? state
      : { ...state, page: analytics, phase: 'idle' as const };
  const page = current.page;

  async function loadNextPage(): Promise<void> {
    if (page.status !== 'available' || page.nextCursor === null || current.phase === 'loading')
      return;

    const request = requestId.current + 1;
    requestId.current = request;
    setState({ ...current, phase: 'loading' });
    try {
      const nextPage = await reader.getAnalyticsPage(page.nextCursor);
      if (
        request === requestId.current &&
        activeScope.current.analytics === analytics &&
        activeScope.current.reader === reader
      ) {
        setState({ analytics, reader, page: nextPage, phase: 'idle' });
      }
    } catch {
      // The error is deliberately converted to visible, retryable UI below;
      // the technical cause remains at the transport boundary.
      if (
        request === requestId.current &&
        activeScope.current.analytics === analytics &&
        activeScope.current.reader === reader
      ) {
        setState({ ...current, phase: 'error' });
      }
    }
  }

  return (
    <MyDataCategoryCard
      heading={t('myData.analytics.heading')}
      ownership={t('myData.ownership.app')}
    >
      {page.status === 'unavailable' ? (
        <MyDataEmptyNote>{t('myData.analytics.unavailable')}</MyDataEmptyNote>
      ) : page.facts.length === 0 ? (
        <MyDataEmptyNote>{t('myData.analytics.empty')}</MyDataEmptyNote>
      ) : (
        <div className="flex flex-col">
          {page.facts.map((fact) => (
            <AnalyticsFact key={factKey(fact)} fact={fact} />
          ))}
        </div>
      )}

      {page.status === 'available' && page.nextCursor !== null && (
        <div className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-3">
          {current.phase === 'error' && (
            <p role="alert" className="text-sm text-destructive">
              {t('myData.analytics.pageError')}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={current.phase === 'loading'}
            onClick={() => {
              void loadNextPage();
            }}
          >
            {current.phase === 'loading'
              ? t('myData.analytics.loadingNext')
              : current.phase === 'error'
                ? t('myData.analytics.retry')
                : t('myData.analytics.nextPage')}
          </Button>
        </div>
      )}
    </MyDataCategoryCard>
  );
}
